// Setup → Possible Duplicates (MOTHER-179): GET /api/merge/duplicates
// (apps/server/src/mergeDuplicates.mjs, MOTHER-177), turned into one row per merge.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export const MERGE_DUPLICATES_API = '/api/merge/duplicates';

/** The objects whose duplicates the route lists, as its `object` names them. */
export type DuplicateObject = 'accounts' | 'contacts';

/**
 * What a person who is not an Admin sees here, without the route being called: the
 * message every other Admin-only Setup page shows them, which is the server's
 * answer to Delivery - External (protectedRoute in apps/server/src/api.mjs).
 */
export const ADMIN_ONLY_MESSAGE = "Your access is limited to your projects' work.";

export interface DuplicateRecord {
  id: string;
  name: string;
  /** An account's domain; '' for a contact. */
  domain: string;
  /** A contact's email; '' for an account. */
  email: string;
  /** A contact's account; '' for an account, or a contact with none. */
  accountId: string;
  accountName: string;
  createdAt: string;
}

export interface DuplicateList {
  object: DuplicateObject;
  /** Every group the route found, listed or not. */
  totalGroups: number;
  /** More groups were found than listed. */
  truncated: boolean;
  /** Each group's records, oldest first, as the route orders them. */
  groups: DuplicateRecord[][];
}

/** One row: one merge the dialog can show, of a group's oldest record and one other. */
export interface DuplicatePair {
  id: string;
  keep: DuplicateRecord;
  mergeAway: DuplicateRecord;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

function parseRecord(record: Record<string, unknown>): DuplicateRecord {
  return {
    id: str(record.id),
    name: str(record.name),
    domain: str(record.domain),
    email: str(record.email),
    accountId: str(record.account_id),
    accountName: str(record.account_name),
    createdAt: str(record.created_at),
  };
}

export function parseDuplicates(object: DuplicateObject, body: unknown): DuplicateList {
  if (!isObject(body) || !Array.isArray(body.groups)) throw new Error('The server sent possible duplicates the page cannot read.');
  return {
    object,
    totalGroups: num(body.total_groups),
    truncated: body.truncated === true,
    groups: body.groups
      .filter(isObject)
      .map((group) => (Array.isArray(group.records) ? group.records.filter(isObject).map(parseRecord) : []))
      .filter((records) => records.length > 1),
  };
}

/**
 * One row per merge: a group of N records gives N-1 rows, each pairing the group's
 * first record, its oldest (the route lists them oldest first), with one other.
 */
export function pairsOf(list: DuplicateList): DuplicatePair[] {
  return list.groups.flatMap(([keep, ...others]) =>
    keep ? others.map((mergeAway) => ({ id: `${keep.id}:${mergeAway.id}`, keep, mergeAway })) : []);
}

export async function listDuplicates(fetcher: Fetch, object: DuplicateObject): Promise<DuplicateList> {
  const response = await fetcher(`${MERGE_DUPLICATES_API}?object=${object}`);
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(isObject(body) && typeof body.error === 'string' && body.error !== '' ? body.error : 'Could not load possible duplicates.');
  }
  return parseDuplicates(object, body);
}
